/**
 * ---------------------------------------------------------------------------------------------------------------------
 * KC CART DRAWER ENGINE
 * ---------------------------------------------------------------------------------------------------------------------
 * Kaching-style cart rewards for Prestige.
 *
 * Design note: every qualification decision (which tier is unlocked, which sample lines are still valid, how far the
 * progress bar should be filled) is made server side in snippets/kc-rewards.liquid. This file is deliberately "dumb" —
 * it only performs cart mutations and then lets the theme re-render the section. That means the UI can never drift out
 * of sync with the real cart, and it keeps working after the drawer HTML is swapped out.
 * ---------------------------------------------------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__kcCartEngine) return;
  window.__kcCartEngine = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var GIFT_PROPERTY = '_kc_gift';

  /* ------------------------------------------------------------------------------------------------------------------
   * Utilities
   * --------------------------------------------------------------------------------------------------------------- */

  function moneyFormat(cents) {
    var format =
      (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) ||
      '${{amount}}';

    function withComma(number, precision, thousands, decimal) {
      if (isNaN(number) || number === null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var head = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (thousands || ','));
      var tail = parts[1] ? (decimal || '.') + parts[1] : '';
      return head + tail;
    }

    function withIndianComma(number, precision) {
      if (isNaN(number) || number === null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var head = parts[0];
      var last3 = head.slice(-3);
      var rest = head.slice(0, -3);
      if (rest) last3 = ',' + last3;
      head = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + last3;
      return head + (parts[1] ? '.' + parts[1] : '');
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, name) {
      switch (name) {
        case 'amount':
          return withComma(cents, 2);
        case 'amount_no_decimals':
          return withComma(cents, 0);
        case 'amount_with_comma_separator':
          return withComma(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withComma(cents, 0, '.', ',');
        case 'amount_with_space_separator':
          return withComma(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return withComma(cents, 0, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return withComma(cents, 2, ' ', '.');
        case 'amount_with_apostrophe_separator':
          return withComma(cents, 2, "'", '.');
        case 'amount_no_decimals_with_apostrophe_separator':
          return withComma(cents, 0, "'", '.');
        case 'amount_with_indian_comma_separator':
          return withIndianComma(cents, 2);
        case 'amount_no_decimals_with_indian_comma_separator':
          return withIndianComma(cents, 0);
        default:
          return withComma(cents, 2);
      }
    });
  }

  function isCartPage() {
    return (
      window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart'
    );
  }

  /**
   * Prestige only re-renders bundled sections, and the cart page section is not one of them — its own quantity
   * controls do a full navigation. So on /cart we reload after a mutation to keep the page truthful.
   */
  var reloadQueued = false;
  function reloadIfCartPage() {
    if (!isCartPage() || reloadQueued) return;
    reloadQueued = true;
    window.location.reload();
  }

  /**
   * The auto-ops counter lives in memory, which a page reload wipes. On the cart page an instruction that can never
   * succeed would therefore reload forever, so the breaker is also kept in sessionStorage, keyed by the instruction
   * payload: the same failing batch is attempted a few times and then abandoned.
   */
  var BREAKER_KEY = 'kc:autoops';

  function breakerCount(signature) {
    try {
      var raw = JSON.parse(window.sessionStorage.getItem(BREAKER_KEY) || 'null');
      return raw && raw.signature === signature ? raw.count : 0;
    } catch (e) {
      return 0;
    }
  }

  function breakerBump(signature) {
    try {
      window.sessionStorage.setItem(
        BREAKER_KEY,
        JSON.stringify({ signature: signature, count: breakerCount(signature) + 1 })
      );
    } catch (e) {
      /* private mode */
    }
  }

  function breakerClear() {
    try {
      window.sessionStorage.removeItem(BREAKER_KEY);
    } catch (e) {
      /* private mode */
    }
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  function loading(state) {
    document.documentElement.dispatchEvent(
      new CustomEvent(state ? 'theme:loading:start' : 'theme:loading:end', { bubbles: true })
    );
  }

  function bundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function announceCart(cart, baseEvent) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: baseEvent || 'line-item:change', cart: cart }
      })
    );
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Cart mutations — mirrors the contract used by Prestige's own cart components
   * --------------------------------------------------------------------------------------------------------------- */

  var busy = false;
  var pendingHousekeeping = false;

  /**
   * While the cart is being corrected, checkout must not be reachable — otherwise a shopper can leave with a sample
   * the cart no longer qualifies for.
   */
  function lockCheckout(locked) {
    var buttons = document.querySelectorAll(
      '[name="checkout"], .kc-checkout-guard, [data-kc-checkout], #gokwik-buy-now, .gokwik-checkout-btn'
    );
    Array.prototype.forEach.call(buttons, function (button) {
      if (locked) {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-busy');
      }
    });

    // Checkout links cannot be disabled by attribute, so CSS neutralises them while we are mid-correction.
    document.documentElement.classList.toggle('kc-cart-busy', !!locked);
  }

  function beginWork() {
    busy = true;
    lockCheckout(true);
    loading(true);
  }

  function endWork() {
    loading(false);
    lockCheckout(false);
    busy = false;
    if (pendingHousekeeping) {
      pendingHousekeeping = false;
      setTimeout(runHousekeeping, 0);
    }
  }

  async function post(endpoint, payload, opts) {
    opts = opts || {};
    var sections = bundledSections();
    var body = Object.assign({}, payload, { sections: sections.join(',') });

    var response = await fetch(ROOT + endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    });

    var json = await response.json().catch(function () {
      return {};
    });

    if (!response.ok) {
      var error = new Error(json.description || json.message || 'Cart request failed');
      error.payload = json;
      throw error;
    }

    var cart;
    if (endpoint.indexOf('cart/add') === 0) {
      cart = await (await fetch(ROOT + 'cart.js')).json();
      cart.sections = json.sections;
    } else {
      cart = json;
    }

    if (!opts.silent) announceCart(cart, opts.baseEvent);
    return cart;
  }

  function addItems(items, opts) {
    return post('cart/add.js', { items: items }, opts);
  }

  function changeLine(key, quantity, opts) {
    return post('cart/change.js', { id: key, quantity: quantity }, opts);
  }

  function refreshCart(opts) {
    return post('cart/update.js', { updates: {} }, opts);
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Discount codes
   * --------------------------------------------------------------------------------------------------------------- */

  async function applyDiscount(code) {
    if (!code) return { ok: false, reason: 'empty' };

    try {
      await fetch(ROOT + 'discount/' + encodeURIComponent(code.trim()), {
        method: 'GET',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin'
      });
    } catch (e) {
      return { ok: false, reason: 'network' };
    }

    var cart = await refreshCart();
    var codes = cart.discount_codes || [];
    var match = codes.filter(function (entry) {
      return String(entry.code).toLowerCase() === String(code).trim().toLowerCase();
    })[0];

    if (!match) return { ok: false, reason: 'invalid', cart: cart };
    if (match.applicable === false) return { ok: false, reason: 'not_applicable', cart: cart };
    return { ok: true, cart: cart };
  }

  async function clearDiscounts() {
    try {
      await fetch(ROOT + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ discount: '' })
      });
    } catch (e) {
      /* ignored — verified against the cart below */
    }
    return refreshCart();
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Housekeeping — executes the removals Liquid asked for
   * --------------------------------------------------------------------------------------------------------------- */

  var autoOps = 0;
  var AUTO_OPS_LIMIT = 5;

  /**
   * Reads every instruction node (the drawer and the cart page can both be on the page, and they render identical
   * payloads), consumes them, and returns a de-duplicated list of values.
   */
  function drainInstructions(attribute) {
    var nodes = document.querySelectorAll('[' + attribute + ']');
    var values = [];
    Array.prototype.forEach.call(nodes, function (node) {
      var parsed;
      try {
        parsed = JSON.parse(node.textContent || '[]');
      } catch (e) {
        parsed = [];
      }
      (parsed || []).forEach(function (value) {
        if (value && values.indexOf(value) === -1) values.push(value);
      });
      node.remove();
    });
    return values;
  }

  async function runHousekeeping() {
    if (busy) {
      pendingHousekeeping = true;
      return;
    }

    var removals = drainInstructions('data-kc-autoclean');
    var clamps = drainInstructions('data-kc-autoclamp');
    var additions = removals.length ? [] : drainInstructions('data-kc-autoadd');

    if (!removals.length && !clamps.length && !additions.length) {
      autoOps = 0;
      breakerClear();
      return;
    }

    var signature = JSON.stringify([removals, clamps, additions]);

    // Two breakers: one for this page's session, one that survives the cart page's reload.
    if (autoOps >= AUTO_OPS_LIMIT || breakerCount(signature) >= AUTO_OPS_LIMIT) {
      return;
    }
    autoOps += 1;
    breakerBump(signature);

    var didMutate = false;
    beginWork();
    try {
      for (var i = 0; i < removals.length; i++) {
        try {
          await changeLine(removals[i], 0, { silent: true });
          didMutate = true;
        } catch (e) {
          /* a stale key just means the line is already gone */
        }
      }

      for (var c = 0; c < clamps.length; c++) {
        try {
          await changeLine(clamps[c], 1, { silent: true });
          didMutate = true;
        } catch (e) {
          /* ditto */
        }
      }

      for (var j = 0; j < additions.length; j++) {
        var parts = String(additions[j]).split('|');
        var variantId = Number(parts[0]);
        var tier = parts[1];
        if (!variantId || !tier) continue;
        var properties = {};
        properties[GIFT_PROPERTY] = tier;
        try {
          await addItems([{ id: variantId, quantity: 1, properties: properties }], { silent: true });
          didMutate = true;
        } catch (e) {
          /* a sample that went out of stock between render and add is not worth interrupting the shopper */
        }
      }

      if (didMutate) {
        breakerClear();
        // refreshCart re-renders the bundled sections; a bare cart.js payload has no `sections` and would break
        // the drawer's replaceContent step.
        await refreshCart();
      }
    } finally {
      endWork();
      // Only reload when the cart genuinely moved — otherwise a permanently failing batch would reload forever.
      if (didMutate) reloadIfCartPage();
    }
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Upsell recommendations top-up
   * --------------------------------------------------------------------------------------------------------------- */

  // Wording for JS-built cards. Overwritten from the block's data attributes so theme settings reach them too.
  var UPSELL_BUTTON_TEXT = 'Add to cart';
  var UPSELL_SAVE_LABEL = 'You save [percent]';
  var UPSELL_VARIANT_LABEL = 'Select [option]:';

  function upsellCardMarkup(product) {
    var variant = (product.variants || []).filter(function (v) {
      return v.available;
    })[0];
    if (!variant) return '';

    var image = product.featured_image || (product.images && product.images[0]) || '';
    var compare = variant.compare_at_price && variant.compare_at_price > variant.price;
    var savePct = compare
      ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
      : 0;

    // Recommendations arrive as JSON, so the option list has to be rebuilt here. Only variants of a multi-variant
    // product get a dropdown — a single-variant product would show a pointless one-entry select.
    var variants = product.variants || [];
    var options = '';
    if (variants.length > 1) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        options +=
          '<option value="' +
          v.id +
          '"' +
          (v.id === variant.id ? ' selected' : '') +
          (v.available ? '' : ' disabled') +
          '>' +
          escapeHtml(v.title) +
          '</option>';
      }
    }

    var optionName = (product.options && product.options[0] && (product.options[0].name || product.options[0])) || '';
    var variantBlock = options
      ? '<p class="kc-upsell-card__variant-label">' +
        escapeHtml(UPSELL_VARIANT_LABEL.replace('[option]', optionName)) +
        '</p>' +
        '<select class="kc-upsell-card__variants" data-kc-variant-select aria-label="' +
        escapeHtml(product.title) +
        ' options">' +
        options +
        '</select>'
      : '';

    return (
      '<div class="kc-upsell-card" data-kc-upsell-card data-kc-product-id="' +
      product.id +
      '">' +
      '<a class="kc-upsell-card__media" href="' +
      escapeHtml(product.url) +
      '" tabindex="-1" aria-hidden="true">' +
      (image
        ? '<img class="kc-upsell-card__image" loading="lazy" src="' + escapeHtml(image) + '" alt="" width="72" height="72">'
        : '<span class="kc-upsell-card__image kc-upsell-card__image--placeholder"></span>') +
      '</a>' +
      '<div class="kc-upsell-card__info">' +
      '<a class="kc-upsell-card__title" href="' +
      escapeHtml(product.url) +
      '">' +
      escapeHtml(product.title) +
      '</a>' +
      '<p class="kc-upsell-card__price">' +
      '<span class="kc-upsell-card__now">' +
      moneyFormat(variant.price) +
      '</span>' +
      (compare ? '<s class="kc-upsell-card__was">' + moneyFormat(variant.compare_at_price) + '</s>' : '') +
      (compare && savePct > 0
        ? '<span class="kc-upsell-card__save">' +
          escapeHtml(UPSELL_SAVE_LABEL.replace('[percent]', savePct + '%')) +
          '</span>'
        : '') +
      '</p>' +
      variantBlock +
      '<button type="button" class="kc-upsell-card__add" data-kc-add-upsell data-kc-variant-id="' +
      variant.id +
      '">' +
      '<span class="kc-upsell-card__add-label">' +
      escapeHtml(UPSELL_BUTTON_TEXT) +
      '</span>' +
      '</button>' +
      '</div>' +
      '</div>'
    );
  }

  async function topUpUpsells() {
    var block = document.querySelector('[data-kc-upsell]');
    if (!block || block.dataset.kcUpsellFilled === 'true') return;

    UPSELL_BUTTON_TEXT = block.dataset.kcUpsellButtonText || UPSELL_BUTTON_TEXT;
    UPSELL_SAVE_LABEL = block.dataset.kcUpsellSaveLabel || UPSELL_SAVE_LABEL;
    UPSELL_VARIANT_LABEL = block.dataset.kcVariantLabel || UPSELL_VARIANT_LABEL;

    var source = block.dataset.kcUpsellSource;
    if (source !== 'recommendations' && source !== 'manual_then_recommendations') return;

    var limit = parseInt(block.dataset.kcUpsellLimit || '4', 10);
    var rendered = parseInt(block.dataset.kcUpsellRendered || '0', 10);
    var seed = block.dataset.kcUpsellSeed;
    var need = limit - rendered;
    if (!seed || need <= 0) return;

    block.dataset.kcUpsellFilled = 'true';

    var hideInCart = block.dataset.kcUpsellHideInCart !== 'false';
    var exclude = hideInCart ? (block.dataset.kcUpsellExclude || '').split(',').filter(Boolean) : [];
    var existing = Array.prototype.map.call(block.querySelectorAll('[data-kc-product-id]'), function (el) {
      return el.dataset.kcProductId;
    });

    try {
      var url =
        ROOT +
        'recommendations/products.json?product_id=' +
        encodeURIComponent(seed) +
        '&limit=10&intent=complementary';
      var data = await (await fetch(url)).json();
      var products = data.products || [];

      if (!products.length) {
        data = await (
          await fetch(
            ROOT + 'recommendations/products.json?product_id=' + encodeURIComponent(seed) + '&limit=10&intent=related'
          )
        ).json();
        products = data.products || [];
      }

      var html = '';
      var added = 0;
      for (var i = 0; i < products.length && added < need; i++) {
        var p = products[i];
        if (exclude.indexOf(String(p.id)) !== -1) continue;
        if (existing.indexOf(String(p.id)) !== -1) continue;
        var markup = upsellCardMarkup(p);
        if (!markup) continue;
        html += markup;
        added += 1;
      }

      if (html) {
        var track = block.querySelector('[data-kc-upsell-track]');
        if (track) track.insertAdjacentHTML('beforeend', html);
        block.removeAttribute('hidden');

        // These cards arrive after the slider last measured itself, so ask it to re-check whether the arrows
        // now have somewhere to scroll to.
        window.dispatchEvent(new Event('resize'));
      }
    } catch (e) {
      /* recommendations are a nice-to-have */
    }
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Interaction handlers
   * --------------------------------------------------------------------------------------------------------------- */

  function setPending(button, pending) {
    if (!button) return;
    button.classList.toggle('is-loading', !!pending);
    if (pending) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('disabled', 'disabled');
    } else {
      button.removeAttribute('aria-busy');
      button.removeAttribute('disabled');
    }
  }

  async function onAddGift(button) {
    if (busy) return;
    var card = button.closest('[data-kc-gift]');
    var picker = button.closest('[data-kc-picker]');
    if (!card || !picker) return;
    if (picker.dataset.kcLocked === 'true') return;

    var select = card.querySelector('[data-kc-variant-select]');
    var variantId = select ? select.value : button.dataset.kcVariantId;
    if (!variantId) return;

    var tier = picker.dataset.kcTier;
    var limit = parseInt(picker.dataset.kcLimit || '1', 10);
    var selected = parseInt(picker.dataset.kcSelected || '0', 10);

    autoOps = 0;
    setPending(button, true);
    beginWork();

    try {
      var properties = {};
      properties[GIFT_PROPERTY] = tier;

      // Add first. When the allowance is one sample this briefly puts two in the cart, and the old one is removed
      // straight after — that way a failed add (sold out, inventory) never leaves the customer with nothing.
      var swapKey = null;
      if (limit === 1 && selected >= 1) {
        var current = picker.querySelector('[data-kc-remove-gift]');
        if (current && current.dataset.kcLineKey) swapKey = current.dataset.kcLineKey;
      }

      await addItems([{ id: Number(variantId), quantity: 1, properties: properties }], { silent: !!swapKey });

      if (swapKey) {
        try {
          await changeLine(swapKey, 0);
        } catch (e) {
          // The replacement is in; housekeeping will clear the surplus on the next render.
          await refreshCart();
        }
      }

      var mechanic = card.dataset.kcMechanic || picker.dataset.kcMechanic;
      var code = card.dataset.kcCode || picker.dataset.kcCode;
      if (mechanic === 'discount' && code) {
        await applyDiscount(code);
      }
    } catch (error) {
      showToast(error && error.message ? error.message : 'Could not add that sample.');
    } finally {
      setPending(button, false);
      endWork();
      reloadIfCartPage();
    }
  }

  async function onRemoveGift(button) {
    if (busy) return;
    var key = button.dataset.kcLineKey;
    if (!key) return;

    autoOps = 0;
    setPending(button, true);
    beginWork();
    try {
      await changeLine(key, 0);
    } catch (e) {
      /* already gone */
    } finally {
      setPending(button, false);
      endWork();
      reloadIfCartPage();
    }
  }

  async function onAddUpsell(button) {
    if (busy) return;
    var card = button.closest('[data-kc-upsell-card]');
    var select = card ? card.querySelector('[data-kc-variant-select]') : null;
    var variantId = select ? select.value : button.dataset.kcVariantId;
    if (!variantId) return;

    autoOps = 0;
    setPending(button, true);
    beginWork();
    try {
      await addItems([{ id: Number(variantId), quantity: 1 }]);
    } catch (error) {
      showToast(error && error.message ? error.message : 'Could not add that product.');
    } finally {
      setPending(button, false);
      endWork();
      reloadIfCartPage();
    }
  }

  async function onApplyDiscount(button) {
    if (busy) return;
    var wrapper = button.closest('[data-kc-discount]');
    if (!wrapper) return;
    var input = wrapper.querySelector('[data-kc-discount-input]');
    var feedback = wrapper.querySelector('[data-kc-discount-feedback]');
    var code = (input && input.value ? input.value : '').trim();

    function say(message, isError) {
      if (!feedback) return;
      feedback.textContent = message;
      feedback.hidden = !message;
      feedback.classList.toggle('is-error', !!isError);
    }

    if (!code) {
      say('Enter a discount code.', true);
      return;
    }

    autoOps = 0;
    setPending(button, true);
    beginWork();
    say('');

    try {
      var result = await applyDiscount(code);
      if (!result.ok) {
        say(
          result.reason === 'not_applicable'
            ? 'That code cannot be used with this cart.'
            : 'That discount code is not valid.',
          true
        );
      }
    } catch (e) {
      say('Something went wrong. Please try again.', true);
    } finally {
      setPending(button, false);
      endWork();
      reloadIfCartPage();
    }
  }

  async function onRemoveDiscount(button) {
    if (busy) return;
    autoOps = 0;
    setPending(button, true);
    beginWork();
    try {
      var cart = await clearDiscounts();
      if ((cart.discount_codes || []).length) {
        showToast('This code stays on the order — you can remove it at checkout.');
      }
    } catch (e) {
      /* noop */
    } finally {
      setPending(button, false);
      endWork();
      reloadIfCartPage();
    }
  }

  var noteTimer = null;
  function onNoteInput(textarea) {
    clearTimeout(noteTimer);
    noteTimer = setTimeout(async function () {
      try {
        await fetch(ROOT + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ note: textarea.value })
        });
        var saved = textarea.parentElement && textarea.parentElement.querySelector('[data-kc-note-saved]');
        if (saved) {
          saved.hidden = false;
          setTimeout(function () {
            saved.hidden = true;
          }, 1800);
        }
      } catch (e) {
        /* noop */
      }
    }, 600);
  }

  function onVariantSelect(select) {
    var card = select.closest('[data-kc-gift], [data-kc-upsell-card]');
    if (!card) return;
    var button = card.querySelector('[data-kc-add-gift], [data-kc-add-upsell]');
    if (button) button.dataset.kcVariantId = select.value;
  }

  function openDrawer() {
    var drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.show === 'function') {
      drawer.show();
    } else {
      window.location.href = ROOT + 'cart';
    }
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Toast
   * --------------------------------------------------------------------------------------------------------------- */

  var toastTimer = null;
  function showToast(message) {
    if (!message) return;
    var toast = document.querySelector('.kc-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'kc-toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 3500);
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Wiring
   * --------------------------------------------------------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var addGift = target.closest('[data-kc-add-gift]');
    if (addGift) {
      event.preventDefault();
      onAddGift(addGift);
      return;
    }

    var removeGift = target.closest('[data-kc-remove-gift]');
    if (removeGift) {
      event.preventDefault();
      onRemoveGift(removeGift);
      return;
    }

    var addUpsell = target.closest('[data-kc-add-upsell]');
    if (addUpsell) {
      event.preventDefault();
      onAddUpsell(addUpsell);
      return;
    }

    var applyBtn = target.closest('[data-kc-apply-discount]');
    if (applyBtn) {
      event.preventDefault();
      onApplyDiscount(applyBtn);
      return;
    }

    var removeDiscount = target.closest('[data-kc-remove-discount]');
    if (removeDiscount) {
      event.preventDefault();
      onRemoveDiscount(removeDiscount);
      return;
    }

    var sticky = target.closest('[data-kc-sticky]');
    if (sticky) {
      event.preventDefault();
      openDrawer();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    var input = event.target.closest && event.target.closest('[data-kc-discount-input]');
    if (!input) return;
    event.preventDefault();
    var wrapper = input.closest('[data-kc-discount]');
    var button = wrapper && wrapper.querySelector('[data-kc-apply-discount]');
    if (button) onApplyDiscount(button);
  });

  document.addEventListener('change', function (event) {
    var select = event.target.closest && event.target.closest('[data-kc-variant-select]');
    if (select) onVariantSelect(select);
  });

  document.addEventListener('input', function (event) {
    var textarea = event.target.closest && event.target.closest('[data-kc-note-input]');
    if (textarea) onNoteInput(textarea);
  });

  function syncSticky(itemCount) {
    var sticky = document.querySelector('[data-kc-sticky]');
    if (!sticky) return;
    var counter = sticky.querySelector('[data-kc-sticky-count]');
    if (counter) counter.textContent = itemCount;
    if (sticky.dataset.kcHideEmpty === 'true') {
      sticky.hidden = itemCount === 0;
    }
  }

  function shopperItemCount(cart) {
    if (!cart || !cart.items) return cart ? cart.item_count : 0;
    return cart.items.reduce(function (total, item) {
      var properties = item.properties || {};
      return properties[GIFT_PROPERTY] ? total : total + item.quantity;
    }, 0);
  }

  document.addEventListener('cart:change', function (event) {
    var cart = event.detail && event.detail.cart;
    if (cart) syncSticky(shopperItemCount(cart));

    // The drawer replaces its own children on this same event; wait a tick so we read the fresh markup.
    setTimeout(function () {
      runHousekeeping();
      topUpUpsells();
      initBanner();
    }, 60);
  });

  /* ------------------------------------------------------------------------------------------------------------------
   * Announcement banner carousel
   * --------------------------------------------------------------------------------------------------------------- */

  var bannerTimer = null;

  function initBanner() {
    var banner = document.querySelector('[data-kc-banner]');
    if (!banner) {
      clearInterval(bannerTimer);
      bannerTimer = null;
      return;
    }

    var slides = banner.querySelectorAll('[data-kc-banner-slide]');
    if (slides.length < 2) return;

    // The drawer re-renders on every cart change, so the index is re-read from the DOM rather than kept in a closure.
    function currentIndex() {
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].classList.contains('is-active')) return i;
      }
      return 0;
    }

    function show(index) {
      var next = (index + slides.length) % slides.length;
      Array.prototype.forEach.call(slides, function (slide, i) {
        var active = i === next;
        slide.classList.toggle('is-active', active);
        if (active) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });
    }

    function step(delta) {
      show(currentIndex() + delta);
      restart();
    }

    function restart() {
      clearInterval(bannerTimer);
      bannerTimer = null;
      if (banner.dataset.kcBannerAutoplay !== 'true') return;
      var speed = parseInt(banner.dataset.kcBannerSpeed || '5000', 10);
      if (!speed || speed < 1500) speed = 5000;
      bannerTimer = setInterval(function () {
        if (!document.body.contains(banner)) {
          clearInterval(bannerTimer);
          bannerTimer = null;
          return;
        }
        show(currentIndex() + 1);
      }, speed);
    }

    banner.__kcStep = step;
    restart();
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var prev = target.closest('[data-kc-banner-prev]');
    var next = target.closest('[data-kc-banner-next]');
    if (!prev && !next) return;

    event.preventDefault();
    var banner = (prev || next).closest('[data-kc-banner]');
    if (banner && typeof banner.__kcStep === 'function') banner.__kcStep(prev ? -1 : 1);
  });

  /* ------------------------------------------------------------------------------------------------------------------
   * Solid header on scroll
   * --------------------------------------------------------------------------------------------------------------- */

  function initHeaderScroll() {
    var root = document.documentElement;
    var ticking = false;

    function apply() {
      root.classList.toggle('kc-scrolled', window.scrollY > 10);
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(apply);
      },
      { passive: true }
    );

    apply();
  }

  /**
   * The /cart page is retired: it bounces back here with ?cart=open, and we finish the job by opening the drawer.
   */
  function openDrawerIfRequested() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('cart') !== 'open') return;

    params.delete('cart');
    var query = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash);

    // The drawer is a custom element; wait for it to be defined before calling show().
    var open = function () {
      var drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.show === 'function') drawer.show();
    };
    if (window.customElements && window.customElements.whenDefined) {
      window.customElements.whenDefined('cart-drawer').then(function () {
        setTimeout(open, 50);
      });
    } else {
      setTimeout(open, 300);
    }
  }

  function boot() {
    runHousekeeping();
    topUpUpsells();
    initBanner();
    initHeaderScroll();
    openDrawerIfRequested();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.KCCart = {
    addItems: addItems,
    changeLine: changeLine,
    refresh: refreshCart,
    applyDiscount: applyDiscount,
    openDrawer: openDrawer,
    loading: loading
  };
})();


/* ---------------------------------------------------------------------------------------------------------------------
 * SAMPLE PICKER SLIDER — one row, arrows either side
 * ------------------------------------------------------------------------------------------------------------------ */
(function () {
  'use strict';

  function syncArrows(slider) {
    var track = slider.querySelector('[data-kc-slider-track]');
    var prev = slider.querySelector('[data-kc-slider-prev]');
    var next = slider.querySelector('[data-kc-slider-next]');
    if (!track) return;

    // A one-pixel tolerance keeps sub-pixel layout rounding from leaving a dead arrow on screen.
    var overflowing = track.scrollWidth - track.clientWidth > 1;
    var atStart = track.scrollLeft <= 1;
    var atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;

    if (prev) prev.hidden = !overflowing || atStart;
    if (next) next.hidden = !overflowing || atEnd;
  }

  function syncAll() {
    var sliders = document.querySelectorAll('[data-kc-slider]');
    for (var i = 0; i < sliders.length; i++) syncArrows(sliders[i]);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-kc-slider-prev], [data-kc-slider-next]');
    if (!button) return;

    var slider = button.closest('[data-kc-slider]');
    var track = slider && slider.querySelector('[data-kc-slider-track]');
    if (!track) return;

    event.preventDefault();
    var step = Math.max(120, Math.round(track.clientWidth * 0.8));
    track.scrollBy({
      left: button.hasAttribute('data-kc-slider-prev') ? -step : step,
      behavior: 'smooth'
    });
  });

  // Scroll does not bubble, so listen in the capture phase and filter to picker tracks.
  document.addEventListener(
    'scroll',
    function (event) {
      var track = event.target;
      if (!track || !track.hasAttribute || !track.hasAttribute('data-kc-slider-track')) return;
      var slider = track.closest('[data-kc-slider]');
      if (slider) syncArrows(slider);
    },
    true
  );

  window.addEventListener('resize', syncAll);
  document.addEventListener('cart:change', function () {
    // The drawer swaps its markup on every cart mutation; re-measure once the new nodes are laid out.
    requestAnimationFrame(syncAll);
  });

  /**
   * The drawer is rendered on page load but hidden, so a track measured then reports a width of zero: nothing
   * looks overflowing, both arrows stay hidden, and a six-card carousel reads as a single product. Watching each
   * track for a size change catches the moment the drawer opens and the track finally has a width to measure.
   */
  var observer = null;
  if (typeof ResizeObserver === 'function') {
    observer = new ResizeObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var slider = entries[i].target.closest('[data-kc-slider]');
        if (slider) syncArrows(slider);
      }
    });
  }

  function observeTracks() {
    if (!observer) return;
    observer.disconnect();
    var tracks = document.querySelectorAll('[data-kc-slider-track]');
    for (var i = 0; i < tracks.length; i++) observer.observe(tracks[i]);
  }

  function refresh() {
    syncAll();
    observeTracks();
  }

  document.addEventListener('cart:change', function () {
    requestAnimationFrame(observeTracks);
  });

  // Belt and braces for browsers without ResizeObserver: any click that could open the drawer re-measures.
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    if (event.target.closest('[href$="/cart"], [data-kc-open-cart], .header__cart-icon, cart-count')) {
      setTimeout(syncAll, 350);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();


/* ---------------------------------------------------------------------------------------------------------------------
 * PICKER COLLAPSE — fold the sample strip once the tier is full, reopen to change the choice
 * ------------------------------------------------------------------------------------------------------------------ */
(function () {
  'use strict';

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest && event.target.closest('[data-kc-picker-toggle]');
    if (!toggle) return;

    var picker = toggle.closest('[data-kc-picker]');
    if (!picker) return;

    event.preventDefault();

    var collapsed = picker.classList.toggle('kc-picker--collapsed');
    toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    toggle.setAttribute('aria-label', collapsed ? 'Change your free sample' : 'Hide the sample list');

    // The slider measures itself against a zero-height box while folded, so re-measure after it opens.
    if (!collapsed) {
      window.setTimeout(function () {
        window.dispatchEvent(new Event('resize'));
      }, 260);
    }
  });
})();


/* ---------------------------------------------------------------------------------------------------------------------
 * CART LINE VARIANT SWAP — change a shade or size without leaving the drawer
 * ------------------------------------------------------------------------------------------------------------------ */
(function () {
  'use strict';

  var busy = false;

  function readMap(wrapper) {
    var node = wrapper.querySelector('[data-kc-variant-map]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent) || [];
    } catch (error) {
      return [];
    }
  }

  /** The selected value of every dropdown on this line, in option order. */
  function chosenOptions(wrapper) {
    var selects = wrapper.querySelectorAll('[data-kc-variant-select]');
    var values = [];
    for (var i = 0; i < selects.length; i++) {
      var index = parseInt(selects[i].getAttribute('data-kc-option-index'), 10) || 0;
      values[index] = selects[i].value;
    }
    return values;
  }

  function matchVariant(variants, wanted) {
    for (var i = 0; i < variants.length; i++) {
      var options = variants[i].options || [];
      if (options.length !== wanted.length) continue;

      var same = true;
      for (var j = 0; j < options.length; j++) {
        if (options[j] !== wanted[j]) {
          same = false;
          break;
        }
      }
      if (same) return variants[i];
    }
    return null;
  }

  document.addEventListener('change', async function (event) {
    var select = event.target.closest && event.target.closest('[data-kc-variant-select]');
    if (!select) return;

    var wrapper = select.closest('[data-kc-line-variants]');
    if (!wrapper || busy) return;

    // The cart helpers live inside the engine's closure above; this block reaches them through the public API.
    // Calling them by bare name threw a ReferenceError, so a changed dropdown never reached the cart.
    var K = window.KCCart;
    if (!K) return;

    var variants = readMap(wrapper);
    var wanted = chosenOptions(wrapper);
    var variant = matchVariant(variants, wanted);

    var currentId = parseInt(wrapper.getAttribute('data-kc-current-variant'), 10) || 0;

    // No such combination, or it is sold out — put the dropdowns back rather than leaving a dead selection on screen.
    if (!variant || variant.available === false || variant.id === currentId) {
      if (!variant || variant.available === false) {
        await K.refresh();
      }
      return;
    }

    var key = wrapper.getAttribute('data-kc-line-key');
    var quantity = parseInt(wrapper.getAttribute('data-kc-line-qty'), 10) || 1;
    var tier = wrapper.getAttribute('data-kc-gift-tier');

    var properties = {};
    if (tier) properties['_kc_gift'] = tier;

    busy = true;
    K.loading(true);

    try {
      // Add the replacement before removing the original: the cart never dips below a reward threshold in between,
      // so the housekeeping pass cannot strip a freebie mid-swap.
      await K.addItems([{ id: variant.id, quantity: quantity, properties: properties }], { silent: true });
      await K.changeLine(key, 0);
    } catch (error) {
      try {
        await K.refresh();
      } catch (ignored) {
        /* the drawer will resync on the next cart change */
      }
    } finally {
      busy = false;
      K.loading(false);
    }
  });
})();
